import { Box, Typography } from '@mui/material';
import { TournamentStatus } from '@okletsplay/core';
import { DateFormat } from 'app/enums';
import { TournamentEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useTournamentAlertSettings } from 'modules/tournaments/hooks';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import moment from 'moment';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { withTournamentStatus } from 'shared/HOC/withTournamentStatus';
import { useStyles as usePulseAnimationStyles } from 'shared/styles/PulseAnimation.styles';
import { BreakTimer } from 'modules/bracket/components/BreakTimer';
import { useStyles } from 'modules/tournaments/styles/TournamentAlert.styles';

const TournamentMatchGameJoinAlertComponent: FC = () => {
  const { classes: pulseAnimationClasses } = usePulseAnimationStyles();
  const { classes } = useStyles();
  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);
  const gameDuration = useSelector(currentTournamentSelector.gameDuration);

  const {
    isLoading,
    onClickHandler,
    matchGameTitle,
    joinMatchGameTimer,
    isMyTeamDisqualified,
    isBreakTimerAvailable,
    activeTournamentMatchGame,
  } = useTournamentAlertSettings();

  const alertTitle = useMemo(() => {
    if (!tournament) {
      return null;
    }

    return `${matchGameTitle} of ${tournament.name} is started!`;
  }, [matchGameTitle, tournament]);

  const description = useMemo(() => {
    if (!gameDuration || !activeTournamentMatchGame?.startedAt) {
      return '';
    }

    const date = moment(activeTournamentMatchGame?.startedAt).format(DateFormat.YyyyMmDdHhMm);
    return `A match should be finished in ${gameDuration} minutes starting from ${date}`;
  }, [gameDuration, activeTournamentMatchGame?.startedAt]);

  if (isEmpty(activeTournamentMatchGame) || isMyTeamDisqualified) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box pb={2}>
        <Typography className={classes.title}>{alertTitle}</Typography>
        {description && <Typography className={classes.description}>{description}</Typography>}
      </Box>
      <LoaderButton
        fullWidth
        color="primary"
        variant="contained"
        loading={isLoading}
        onClick={onClickHandler}
        className={classNames(pulseAnimationClasses.pulse, classes.button)}
      >
        <>
          <Typography pr={1}>Play Now</Typography>
          {isBreakTimerAvailable && (
            <BreakTimer
              prefix=""
              startedAt={activeTournamentMatchGame.startedAt as string}
              duration={joinMatchGameTimer}
            />
          )}
        </>
      </LoaderButton>
    </Box>
  );
};

export const TournamentMatchGameJoinAlert = withTournamentStatus(
  TournamentMatchGameJoinAlertComponent,
  [TournamentStatus.Live]
);
